import { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import * as d3 from "d3";
import { fetchAPI } from "../../../config/api";
import { toDivisionApi } from "../../../config/constants";
import {
  filterEvents,
  computeAggregates,
  formatAvg,
  normalizeZone
} from "../../../utils/sprayChartUtils";
import { drawInfield, drawOutfield } from "./components/field-drawing";

const WIDTH = 800;
const HEIGHT = 500;

const getFilterText = (handFilter, selectedZones) => {
  const zoneText = selectedZones.length === 0
    ? "All zones"
    : `Zones: ${selectedZones.map(normalizeZone).join(", ")}`;
  const handText = handFilter === "all" ? "" : (handFilter === "rhp" ? "vs RHP" : "vs LHP");
  return handText ? `${zoneText} • ${handText}` : zoneText;
};

export const SprayChartReport = () => {
  const [searchParams] = useSearchParams();
  const svgRef = useRef();
  const [ready, setReady] = useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [error, setError] = useState("");

  const injectedConfig = window.__SPRAY_CHART_CONFIG__ || null;
  const playerId = injectedConfig?.player_id || searchParams.get("player_id");
  const year = parseInt(String(injectedConfig?.year || searchParams.get("year")), 10);
  const division = toDivisionApi(injectedConfig?.division || searchParams.get("division"));
  const handFilter = injectedConfig?.hand_filter || searchParams.get("hand_filter") || "all";
  const selectedZonesParam = injectedConfig?.selected_zones || searchParams.get("selected_zones");

  const selectedZones = useMemo(
    () => (selectedZonesParam ? selectedZonesParam.split(",") : []),
    [selectedZonesParam]
  );

  useEffect(() => {
    if (!playerId) return;

    const fetchData = async () => {
      setReady(false);
      setError("");
      try {
        const data = await fetchAPI(
          `/spraychart_data/${playerId}?year=${year}&division=${division}`
        );

        if (!data) {
          setError("No spray chart data returned.");
          setReady(true);
          return;
        }

        const bats = data.bats || "-";
        const team = data.team_name || "-";
        const playerInfo = `${bats ? bats.substring(0, 1).toUpperCase() : "-"} | ${year} | ${team}`;

        setPlayerData({
          player_name: data.player_name || "-",
          playerInfo,
        });

        const events = Array.isArray(data.events) ? data.events : [];
        const filteredEvents = filterEvents(events, handFilter, selectedZones);
        const agg = computeAggregates(filteredEvents.length ? filteredEvents : events);
        setAggregates(agg);
      } catch (err) {
        console.error("Error fetching data:", err);
        setAggregates(null);
        setPlayerData(null);
        setError(err?.message || "Failed to load spray chart data.");
        setReady(true);
      }
    };

    fetchData();
  }, [playerId, year, division, handFilter, selectedZones]);

  useEffect(() => {
    if (!svgRef.current || !aggregates) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);

    const { outfieldZones: outfieldZoneData, infieldZones: infieldZoneData, stats } = aggregates;

    const margin = { top: 100, right: 20, bottom: 60, left: 20 };
    const innerWidth = WIDTH - margin.left - margin.right;
    const innerHeight = HEIGHT - margin.top - margin.bottom;

    svg.append("rect")
      .attr("width", WIDTH)
      .attr("height", HEIGHT)
      .attr("fill", "#F5FBFF");

    const header = svg.append("g").attr("transform", "translate(20, 20)");

    header.append("text")
      .attr("x", 0)
      .attr("y", 24)
      .attr("font-size", "24px")
      .attr("font-weight", "bold")
      .attr("fill", "#1a1a1a")
      .text(playerData?.player_name);

    header.append("text")
      .attr("x", 0)
      .attr("y", 48)
      .attr("font-size", "14px")
      .attr("fill", "#666")
      .text(playerData?.playerInfo || "");

    const filterText = getFilterText(handFilter, selectedZones);
    header.append("text")
      .attr("x", 0)
      .attr("y", 68)
      .attr("font-size", "12px")
      .attr("fill", "#888")
      .text(filterText);

    const field = svg.append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const centerX = innerWidth / 2;
    const centerY = innerHeight * 0.95;
    const arcRadius = Math.min(innerWidth * 0.42, innerHeight * 0.9);
    const infieldRadius = arcRadius * 0.52;

    drawInfield(field, { centerX, centerY, infieldRadius, infieldZoneData, selectedZones });
    drawOutfield(field, { centerX, centerY, arcRadius, infieldRadius, outfieldZoneData, selectedZones });

    const statsGroup = svg.append("g")
      .attr("transform", `translate(${WIDTH - 200}, 25)`);

    const statsData = [
      { label: "AVG", value: formatAvg(stats.battingAvg) },
      { label: "OBP", value: formatAvg(stats.onBasePercentage) },
      { label: "wOBA", value: formatAvg(stats.wOBA) },
      { label: "PA", value: stats.PA },
    ];

    statsData.forEach((stat, i) => {
      statsGroup.append("text")
        .attr("x", i * 45)
        .attr("y", 0)
        .attr("font-size", "10px")
        .attr("fill", "#666")
        .attr("text-anchor", "middle")
        .text(stat.label);

      statsGroup.append("text")
        .attr("x", i * 45)
        .attr("y", 18)
        .attr("font-size", "14px")
        .attr("font-weight", "bold")
        .attr("fill", "#1a1a1a")
        .attr("text-anchor", "middle")
        .text(stat.value);
    });

    svg.append("text")
      .attr("x", WIDTH - 10)
      .attr("y", HEIGHT - 10)
      .attr("font-size", "10px")
      .attr("fill", "#aaa")
      .attr("text-anchor", "end")
      .text("d3-dashboard.com");

    setReady(true);
  }, [aggregates, playerData, selectedZones, handFilter]);

  if (!playerId) {
    return <div>Missing player_id parameter</div>;
  }

  if (error) {
    return (
      <div
        data-export-ready="true"
        data-chart-container
        style={{
          width: `${WIDTH}px`,
          height: `${HEIGHT}px`,
          backgroundColor: "#F5FBFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#374151",
          padding: "24px",
          textAlign: "center",
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      data-export-ready={ready ? "true" : "false"}
      data-chart-container
      style={{
        width: `${WIDTH}px`,
        height: `${HEIGHT}px`,
        backgroundColor: "#F5FBFF",
      }}
    >
      <svg
        ref={svgRef}
        width={WIDTH}
        height={HEIGHT}
        style={{ display: "block" }}
      />
    </div>
  );
};
